/**
 * ContextMenu — right-click context menu for the editor canvas.
 *
 * Listens for `editor:contextMenu` events from EditorInputSystem and
 * renders a small menu at the cursor position with actions appropriate
 * to what's under the cursor (entity, tile, or empty space).
 *
 * Actions:
 * - Entity: Delete, Properties, Duplicate
 * - Tile: Delete, Properties, Copy
 * - Empty: Paste, Cancel
 *
 * Clicking an action dispatches to EditorSystem methods. Clicking
 * outside the menu closes it.
 */

import { useCallback, useEffect, useState } from 'react';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { EditorSystem } from '../../../systems/editor/EditorSystem';
import { TileType } from '../../../../shared/TileEditorTypes';
import type { LevelEntity } from '../../../../shared/CharacterTypes';

interface ContextMenuState {
  screenX: number;
  screenY: number;
  tx: number;
  ty: number;
  entity: LevelEntity | null;
  tileType: number;
  layer: string;
}

export function ContextMenu() {
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  const getEditor = useCallback(() => {
    return ServiceRegistry.getInstance().get('EditorSystem', { optional: true }) as EditorSystem | undefined;
  }, []);

  useEffect(() => {
    const editor = getEditor();
    if (!editor) return;

    const handler = (data: ContextMenuState) => {
      setMenu(data);
    };

    const disposer = editor.on('editor:contextMenu', handler);
    return disposer;
  }, [getEditor]);

  // Close on any click outside the menu, or Escape
  useEffect(() => {
    if (!menu) return;

    const closeHandler = () => setMenu(null);
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };

    // Delay attaching so the right-click that opened the menu doesn't immediately close it
    const timer = setTimeout(() => {
      window.addEventListener('pointerdown', closeHandler);
      window.addEventListener('keydown', escHandler);
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointerdown', closeHandler);
      window.removeEventListener('keydown', escHandler);
    };
  }, [menu]);

  const handleAction = useCallback((action: string) => {
    const editor = getEditor();
    if (!editor || !menu) return;

    switch (action) {
      case 'delete-entity':
        if (menu.entity) editor.deleteEntity(menu.entity.id);
        break;
      case 'duplicate-entity':
        if (menu.entity) editor.duplicateSelection();
        break;
      case 'select-entity':
        if (menu.entity) editor.selectEntityAt(menu.tx, menu.ty);
        break;
      case 'delete-tile':
        editor.clearSelectedTile();
        break;
      case 'select-tile':
        editor.selectTileAt(menu.tx, menu.ty, menu.layer as any, menu.tileType as TileType);
        break;
      case 'copy-tile':
        editor.copySelection();
        break;
      case 'paste':
        editor.pasteSelection();
        break;
    }
    setMenu(null);
  }, [getEditor, menu]);

  if (!menu) return null;

  const hasEntity = !!menu.entity;
  const hasTile = !hasEntity && menu.tileType !== TileType.EMPTY;

  // Clamp menu position to viewport
  const menuWidth = 180;
  const menuHeight = hasEntity ? 120 : hasTile ? 100 : 50;
  const x = Math.min(menu.screenX, window.innerWidth - menuWidth - 10);
  const y = Math.min(menu.screenY, window.innerHeight - menuHeight - 10);

  const items: { label: string; action: string; icon: string; danger?: boolean }[] = [];

  if (hasEntity) {
    items.push({ label: 'Select Entity', action: 'select-entity', icon: '🎯' });
    items.push({ label: 'Duplicate', action: 'duplicate-entity', icon: '📋' });
    items.push({ label: 'Delete Entity', action: 'delete-entity', icon: '🗑️', danger: true });
  } else if (hasTile) {
    items.push({ label: 'Select Tile', action: 'select-tile', icon: '🎯' });
    items.push({ label: 'Copy Tile', action: 'copy-tile', icon: '📋' });
    items.push({ label: 'Delete Tile', action: 'delete-tile', icon: '🗑️', danger: true });
  } else {
    items.push({ label: 'Paste Here', action: 'paste', icon: '📋' });
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        width: menuWidth,
        background: '#1e2535',
        border: '1px solid #3a4a5e',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        zIndex: 10000,
        overflow: 'hidden',
        fontSize: '13px',
        fontFamily: 'monospace',
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Header showing what was clicked */}
      <div style={{
        padding: '6px 10px',
        borderBottom: '1px solid #2a3a4e',
        color: '#7a8a9e',
        fontSize: '11px',
      }}>
        {hasEntity ? `Entity: ${menu.entity!.type}` :
         hasTile ? `Tile at (${menu.tx}, ${menu.ty})` :
         `Empty (${menu.tx}, ${menu.ty})`}
      </div>

      {items.map(({ label, action, icon, danger }) => (
        <div
          key={action}
          onClick={() => handleAction(action)}
          style={{
            padding: '8px 10px',
            cursor: 'pointer',
            color: danger ? '#e74c3c' : '#ddd',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background 0.1s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#2a3a5e'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ fontSize: '14px' }}>{icon}</span>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}
